/**
 * DayAtAGlance — BLOCK level (Claude Design round 7). The right pane of the new-design Today screen,
 * from real data only: the three bucket counts, the next free gap (from the appointments' real start
 * and END times, lib/today-overview.ts), and every appointment of the day by time with its status.
 * Clicking a row selects that appointment. No KPIs, no revenue, no AI copy.
 */
import { cn } from '@scripsteam/scrips-signal-ds';
import { V2_CARD_SURFACE, V2_EYEBROW, V2_META, V2_BODY_EMPHASIS, V2_META_STRONG } from '@/shared/ui/design-v2';
import { formatApptTime } from '../lib/to-worklist-card';
import type { DayGap } from '../lib/today-overview';
import { TodayStatusChip } from '../components/today-status-chip';
import { glyphKindForAppointment, visitEnded } from '../lib/appointment-glyph';
import type { TodayItem } from './today-types';
import { Calendar, ChevronRight } from 'lucide-react';

const MODALITY = { office: 'Office', home: 'Home visit', virtual: 'Virtual' } as const;

export interface DayAtAGlanceProps {
  /** The clock a running visit is judged by (an unsigned draft reads "Note to sign" only after its end). Default: now. */
  nowMs?: number;
  /** Every appointment of the day, earliest first. */
  items: TodayItem[];
  counts: { upcoming: number; inProgress: number; completed: number };
  gap: DayGap | undefined;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  /** Collapsed to its narrow rail (D-41); only offered where the pane sits beside the centre. */
  collapsed: boolean;
  onToggle: () => void;
}

/** The worklist card's own time format ("10:00a"), so the timeline reads like the cards. */
const hhmm = (iso: string) => formatApptTime(iso) ?? '';

export function DayAtAGlance({
  items,
  counts,
  gap,
  selectedId,
  onSelect,
  collapsed,
  onToggle,
  nowMs = Date.now(),
}: DayAtAGlanceProps) {
  if (collapsed) {
    return (
      <div data-slot="today-day-rail" className="flex h-full flex-col items-center gap-3 py-3">
        <button
          type="button"
          onClick={onToggle}
          aria-label="Expand day at a glance"
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border-0 bg-transparent text-brand-primary hover:bg-surface-background cursor-pointer"
        >
          <Calendar className="size-5" />
        </button>
      </div>
    );
  }
  return (
    <div data-slot="today-day-at-a-glance" className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className={cn('m-0', V2_EYEBROW)}>Day at a glance</h2>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse day at a glance"
          className="hidden xl:inline-flex h-6 w-6 items-center justify-center rounded-full border-0 bg-transparent text-text-secondary hover:text-text-primary cursor-pointer"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className={cn('grid grid-cols-3 rounded-card', V2_CARD_SURFACE)}>
        {(
          [
            ['Upcoming', counts.upcoming],
            ['In session', counts.inProgress],
            ['Completed', counts.completed],
          ] as const
        ).map(([label, n], i) => (
          <div
            key={label}
            className={cn(
              'flex min-w-0 flex-col gap-1 px-4 py-3',
              i > 0 && 'border-0 border-l border-solid border-border-default',
            )}
          >
            <span className="text-xl font-semibold tabular-nums text-text-primary">{n}</span>
            <span className={cn('whitespace-nowrap', V2_META)}>{label}</span>
          </div>
        ))}
      </div>
      {gap && (
        <div data-slot="today-next-gap" className={cn('flex flex-col gap-1 rounded-card px-4 py-3', V2_CARD_SURFACE)}>
          <span className={V2_EYEBROW}>Next gap</span>
          <span className={cn('tabular-nums text-text-primary', V2_BODY_EMPHASIS)}>
            {hhmm(gap.start)} → {hhmm(gap.end)} · {gap.minutes} min
          </span>
        </div>
      )}
      {items.length > 0 && (
        <div className={cn('rounded-card p-2', V2_CARD_SURFACE)}>
          <ol aria-label="Today's appointments by time" className="m-0 p-0 list-none flex flex-col gap-1">
            {items.map((it) => {
              const on = it.id === selectedId;
              const c = it.card;
              const where = c.room || (c.modality ? MODALITY[c.modality] : undefined);
              return (
                <li key={it.id} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onSelect(it.id)}
                    aria-current={on ? 'true' : undefined}
                    className={cn(
                      'flex w-full min-w-0 items-start gap-3 rounded-lg border-0 px-2 py-1.5 text-left cursor-pointer hover:bg-surface-background',
                      on ? 'bg-surface-tint-brand' : 'bg-transparent',
                    )}
                  >
                    <span
                      className={cn(
                        'w-12 shrink-0 tabular-nums',
                        V2_META_STRONG,
                        it.bucket === 'completed' ? 'text-text-secondary' : 'text-text-primary',
                      )}
                    >
                      {hhmm(it.start)}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className={cn('break-words text-text-primary', V2_BODY_EMPHASIS)}>{c.patientName}</span>
                      <span className={cn('break-words', V2_META)}>
                        {[where, c.reasonForVisit].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <TodayStatusChip kind={glyphKindForAppointment(it.statusCode, it.bucket, visitEnded(it, nowMs))}>{c.status}</TodayStatusChip>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
